import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import { Anchor, Box, Center, Group, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconFileTypePdf } from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';

import {
  DdtSortBy,
  GetDdtRequestParams,
  useGetDdtCountQuery,
  useGetDdtQuery,
} from '@api/ddt.api';

import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';
import { Ddt as DdtInterface } from '@interfaces/ddt.interface';

export default function Ddt() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [searchParams] = useSearchParams();

  const selectedDdt = searchParams.get('sel');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetDdtRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'id',
    sortOrder: 'desc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<DdtInterface>
  >({
    columnAccessor: 'id',
    direction: 'desc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as DdtSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);
  // ==========================================================================
  // Api
  // ==========================================================================

  // Get initial orders count
  const { data: ddtCount = { count: 0 }, isLoading: isLoadingDdtCount } =
    useGetDdtCountQuery(searchQuery);

  const { data = [], isLoading } = useGetDdtQuery({
    ...filters,
    searchQuery,
  });

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Documenti di trasporto',
    });
  }, [setLayoutProps]);

  return isLoading || isLoadingDdtCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : (
    <>
      <Group mb="lg">
        <SearchBar
          placeholder="Cerca numero DDT"
          value={filters.searchQuery}
          onChange={(newValue) =>
            setFilters({ ...filters, searchQuery: newValue })
          }
        />
      </Group>
      <DataTable
        striped
        withTableBorder
        noRecordsText="Nessun DDT trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'date',
            title: 'Data',
            sortable: true,
            render: (record) => (
              <Box fw={(record.id === selectedDdt && 'bold') || undefined}>
                {dateToDateString(new Date(record.date))}
              </Box>
            ),
          },
          {
            accessor: 'id',
            title: 'Numero DDT',
            sortable: true,
            render: (record) => (
              <Box fw={(record.id === selectedDdt && 'bold') || undefined}>
                {record.id}
              </Box>
            ),
          },
          {
            accessor: 'url',
            title: 'Pdf',
            render: (record) => (
              <Anchor
                href={record.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                <IconFileTypePdf />
              </Anchor>
            ),
          },
        ]}
        records={data}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={ddtCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
